import { useState } from "react";
import { Link } from "@inertiajs/react";
import { Image as ImageIcon, User, Globe, Twitch, Save } from "lucide-react";
import { Page, Status } from "../pages/Pages";
import TiptapEditor from "./TiptapEditor";
import { StaffMember } from "../pages/Staff";
import { Match } from "../pages/Matches";
import { Opponent } from "../pages/Opponents";

type MatchFormProps = {
    onSubmit: (data: any) => void;
    match?: Match | null;
    opponents: Opponent[];

};

export default function MatchForm({ onSubmit, match, opponents }: MatchFormProps) {
    const isNew = match === null || match === undefined;

    const [form, setForm] = useState({
        url: match?.url ?? "",
        opponentId: match?.opponent_id ?? "",
        scoreTeam: match?.score_team ?? "",
        scoreOpponent: match?.score_opponent ?? "",
        date: match?.date ?? "",
        status: match?.status ?? "",
        bestOf: match?.best_of ?? "",
        event: match?.event ?? "",
    });

    function updateField(key: string, value: string | number) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        onSubmit(form);
    }

    const inputClass =
        "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100";

    const labelClass = "mb-2 block text-sm font-medium text-gray-700";

    return (
        <form onSubmit={handleSubmit} className="mx-auto w-full max-w-4xl">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-200 px-6 py-5 sm:px-8">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <h2 className="text-2xl font-semibold text-gray-900">
                                {isNew ? "Create Match" : "Edit Match"}
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                Manage match information, details, and other settings.
                            </p>
                        </div>

                        <Link
                            href="/admin/matches"
                            className="inline-flex items-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                        >
                            Cancel
                        </Link>
                    </div>
                </div>

                <div className="grid gap-8 px-6 py-6 sm:px-8 lg:grid-cols-[1fr_320px]">
                    <div className="space-y-8">
                        <section className="space-y-5">
                            <div className="flex items-center gap-2">
                                <User size={18} className="text-gray-500" />
                                <h3 className="text-base font-semibold text-gray-900">Basic info</h3>
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label className={labelClass}>Url</label>
                                    <input
                                        type="text"
                                        value={form.url}
                                        onChange={(e) => updateField("url", e.target.value)}
                                        className={inputClass}
                                        placeholder="Url"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Opponent</label>
                                    <select
                                        value={form.opponentId}
                                        onChange={(e) => updateField("opponentId", e.target.value)}
                                        className={inputClass}
                                    >
                                        <option value="">Select Opponent</option>
                                        {opponents.map((opponent) => (
                                            <option key={opponent.id} value={opponent.id}>
                                                {opponent.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Score LAG</label>
                                    <input
                                        type="text"
                                        value={form.scoreTeam}
                                        onChange={(e) => updateField("scoreTeam", e.target.value)}
                                        className={inputClass}
                                        placeholder="Score LAG"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Score Opponent</label>
                                    <input
                                        type="text"
                                        value={form.scoreOpponent}
                                        onChange={(e) => updateField("scoreOpponent", e.target.value)}
                                        className={inputClass}
                                        placeholder="Score Opponent"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Date</label>
                                    <input
                                        type="datetime-local"
                                        value={form.date}
                                        onChange={(e) => updateField("date", e.target.value)}
                                        className={inputClass}
                                        placeholder="Date"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Status</label>
                                    <select
                                        value={form.status}
                                        onChange={(e) => updateField("status", e.target.value)}
                                        className={inputClass}
                                    >
                                        <option value="scheduled">Scheduled</option>
                                        <option value="ongoing">Ongoing</option>
                                        <option value="completed">Completed</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Best Of</label>
                                    <select
                                        value={form.bestOf}
                                        onChange={(e) => updateField("bestOf", e.target.value)}
                                        className={inputClass}
                                    >
                                        <option value="1">1</option>
                                        <option value="3">3</option>
                                        <option value="5">5</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Event</label>
                                    <input
                                        type="text"
                                        value={form.event}
                                        onChange={(e) => updateField("event", e.target.value)}
                                        className={inputClass}
                                        placeholder="Event"
                                    />
                                </div>
                            </div>
                        </section>
                    </div>

                </div>

                <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-5 sm:flex-row sm:justify-end sm:px-8">
                    <Link
                        href="/admin/matches"
                        className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                    >
                        Cancel
                    </Link>

                    <button
                        type="submit"
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                    >
                        <Save size={16} />
                        {isNew ? "Create Match" : "Save Changes"}
                    </button>
                </div>
            </div>
        </form>
    );
}